import { Controller } from '@hotwired/stimulus'

// Single-job cancel modal (design-system). Attached to the same element as the
// `modal` controller. Validates the reason, toggles the rebook fields and
// cancels the job via fetch (mirrors the bulk-cancel modal behaviour).
export default class extends Controller {
  static targets = [
    'form',
    'jobId',
    'jobLabel',
    'reason',
    'customerResponse',
    'satisfaction',
    'rebookCheckbox',
    'rebookFields',
    'cancelBtn',
    'rebookBtn',
    'returnPath',
    'rebookDate',
    'rebookStartTime',
    'rebookEndTime',
    'workListCategory'
  ]

  connect() {
    this.validate()
  }

  open({ jobId, invoice, startTime, endTime } = {}) {
    // Reset the form to a clean state before opening.
    this.formTarget.reset()

    if (jobId) {
      this.jobIdTarget.value = jobId
      this.formTarget.action = `/jobs/${jobId}/cancel`
      this.jobLabelTarget.textContent = invoice ? `Job: #${invoice}` : ''
    }

    this.clearDatepicker(this.rebookDateTarget)
    this.clearDatepicker(this.rebookStartTimeTarget)
    this.clearDatepicker(this.rebookEndTimeTarget)

    this.setDatepickerValue(this.rebookStartTimeTarget, startTime)
    this.setDatepickerValue(this.rebookEndTimeTarget, endTime)

    this.toggleRebook()
    this.validate()
    this.modalController.open()
  }

  validate() {
    const hasReason = this.reasonTarget.value.trim().length > 0
    this.cancelBtnTarget.disabled = !hasReason
    this.rebookBtnTarget.disabled = !hasReason
  }

  toggleRebook() {
    const rebook = this.rebookCheckboxTarget.checked
    this.rebookFieldsTarget.classList.toggle('hidden', !rebook)
    this.cancelBtnTarget.style.display = rebook ? 'none' : ''
    this.rebookBtnTarget.style.display = rebook ? '' : 'none'
  }

  async submit(event) {
    event.preventDefault()
    if (window.tinymce) window.tinymce.triggerSave()

    const jobId = this.jobIdTarget.value
    if (!jobId) {
      this.modalController.close()
      return
    }

    const token = this.csrfToken()
    const body = {
      authenticity_token: token,
      reason: this.reasonTarget.value,
      customer_response: this.customerResponseTarget.value,
      satisfaction_level: this.satisfactionTarget.value,
      rebooked: this.rebookCheckboxTarget.checked ? '1' : '0',
      rebook_date: this.rebookDateTarget.value || '',
      rebook_start_time: this.rebookStartTimeTarget.value || '',
      rebook_end_time: this.rebookEndTimeTarget.value || '',
      rebook_work_list_category_id: this.workListCategoryTarget.value || '',
      return_path: this.returnPathTarget.value
    }

    this.cancelBtnTarget.disabled = true
    this.rebookBtnTarget.disabled = true

    const response = await fetch(`/jobs/${jobId}/cancel`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-CSRF-Token': token },
      body: new URLSearchParams(body)
    })

    if (response.ok) {
      this.modalController.close()
      window.location.reload()
    } else {
      this.cancelBtnTarget.disabled = false
      this.rebookBtnTarget.disabled = false
      alert('Could not cancel the job.')
    }
  }

  setDatepickerValue(input, value) {
    if (!input || !value) return
    const wrapper = input.closest('[data-controller~="datepicker"]')
    if (!wrapper) return
    const datepicker = this.application.getControllerForElementAndIdentifier(wrapper, 'datepicker')
    if (!datepicker || !datepicker.instance) return
    const date = new Date(value)
    if (!isNaN(date.getTime())) datepicker.instance.setDate(date)
  }

  clearDatepicker(input) {
    if (!input) return
    const wrapper = input.closest('[data-controller~="datepicker"]')
    if (!wrapper) return
    const datepicker = this.application.getControllerForElementAndIdentifier(wrapper, 'datepicker')
    if (datepicker && datepicker.instance) datepicker.instance.clear()
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
  }

  get modalController() {
    return this.application.getControllerForElementAndIdentifier(this.element, 'modal')
  }
}
